import { Alert } from '@ordo/ui/alert'
import { ArrowRight, CheckCircle2, X, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useSubstitutionRequestsQuery } from '@/api/substitution-requests'
import { Button } from '@/components/chefs/button'
import { useActiveKitchen } from '@/hooks/chefs/useActiveKitchen'
import { useSubstitutionModal } from '@/hooks/chefs/useSubstitutionModal'
import { toUiSubstitutions } from '@/lib/chefs/substitution-adapters'

const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000

export function SubstitutionResolutionBanner() {
  const kitchenLocationId = useActiveKitchen()
  const subsQuery = useSubstitutionRequestsQuery(
    { kitchenLocationId: kitchenLocationId ?? '' },
    { enabled: Boolean(kitchenLocationId) },
  )
  const openModal = useSubstitutionModal((s) => s.openModal)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

  const items = useMemo(() => {
    const ui = toUiSubstitutions(subsQuery.data?.requests ?? [])
    const now = Date.now()
    return ui.filter((s) => {
      if (s.status !== 'approved' && s.status !== 'rejected') {
        return false
      }
      if (!s.resolvedAt) {
        return false
      }
      if (now - new Date(s.resolvedAt).getTime() > RECENT_WINDOW_MS) {
        return false
      }
      return !dismissedIds.has(s.id)
    })
  }, [subsQuery.data, dismissedIds])

  if (items.length === 0) {
    return null
  }

  function ackResolved() {
    setDismissedIds((prev) => {
      const next = new Set(prev)
      for (const item of items) {
        next.add(item.id)
      }
      return next
    })
  }

  const approved = items.filter((s) => s.status === 'approved')
  const rejected = items.filter((s) => s.status === 'rejected')
  const allApproved = rejected.length === 0
  const allRejected = approved.length === 0

  const variant = allRejected ? 'error' : allApproved ? 'success' : 'info'
  const Icon = allRejected ? XCircle : CheckCircle2

  let body: React.ReactNode
  if (items.length === 1) {
    const sub = items[0]!
    body =
      sub.status === 'approved' ? (
        <span>
          Substitution approved: <span className="font-medium">{sub.ingredientName}</span>{' '}
          <ArrowRight className="inline h-3.5 w-3.5 align-baseline" strokeWidth={2} />{' '}
          <span className="font-medium">{sub.replacement}</span>
        </span>
      ) : (
        <span>
          Substitution rejected: <span className="font-medium">{sub.ingredientName}</span>
          {sub.rejectionReason ? <span className="opacity-90"> — {sub.rejectionReason}</span> : null}
        </span>
      )
  } else {
    body = (
      <span>
        {items.length} substitutions resolved ({approved.length} approved, {rejected.length} rejected).
      </span>
    )
  }

  return (
    <div className="px-4 pt-4 lg:px-4 lg:pt-5 print:hidden">
      <Alert variant={variant} className="flex items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
        <div className="flex-1 text-sm">
          <div className="font-medium">Substitution update</div>
          <div className="mt-0.5 opacity-90">{body}</div>
        </div>
        <button
          type="button"
          className="text-xs font-medium underline-offset-4 hover:underline"
          onClick={() => openModal({ view: 'ledger' })}>
          View details
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Dismiss"
          className="-mt-1 text-muted-foreground"
          onClick={ackResolved}>
          <X className="h-4 w-4" />
        </Button>
      </Alert>
    </div>
  )
}
